<template>
  <gg-card>
    <gg-card-text class="pa-3">
      <div class="d-flex flex-wrap align-center mb-3">
        <h2 class="vds-metrics__heading body-1 mr-auto">
          <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-chart-line</v-icon>
          <span class="ml-2">Metrics</span>
        </h2>
        <v-btn-toggle
          v-model="range"
          mandatory
          dense
          class="vds-metrics__range"
          role="group"
          aria-label="Metrics time range"
          @change="loadMetrics"
        >
          <v-btn
            v-for="r in ranges"
            :key="r"
            :value="r"
            small
            :aria-label="rangeLabels[r] || r"
            :aria-pressed="r === range ? 'true' : 'false'"
          >{{ r }}</v-btn>
        </v-btn-toggle>
      </div>

      <!-- Visually-hidden live region announces load / refresh status to AT. -->
      <span class="vds-metrics__sr-status" aria-live="polite">{{ statusMessage }}</span>

      <!-- Backend doesn't serve history for this service. -->
      <v-alert v-if="!capable" type="info" border="left" dense class="mb-0">
        Metric history isn't available for this server.
      </v-alert>

      <div v-else-if="loadingMetrics" class="py-8 text-center">
        <v-progress-circular indeterminate color="primary" size="36" width="3" aria-hidden="true" />
        <p class="vds-metrics__loading mt-3 mb-0">Loading metrics…</p>
      </div>

      <v-alert v-else-if="error" type="error" border="left" dense class="mb-0" role="alert">
        {{ error.message || 'Metrics are temporarily unavailable.' }}
        <div class="mt-2">
          <gg-btn small tile color="white" class="error--text" @click="loadMetrics">
            <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Retry
          </gg-btn>
        </div>
      </v-alert>

      <v-row v-else>
        <v-col v-for="g in graphs" :key="g.key" cols="12" md="6">
          <div class="vds-graph pa-3">
            <div class="d-flex align-center mb-2">
              <span class="vds-graph__title body-2 mr-auto">{{ g.title }}</span>
              <span class="vds-graph__current">{{ g.current }}</span>
            </div>
            <div
              v-if="g.values.length"
              class="vds-graph__chart"
              role="img"
              :aria-label="chartLabel(g)"
            >
              <v-sparkline
                :value="g.values"
                :gradient="gradient"
                :smooth="8"
                :line-width="1"
                :padding="8"
                :auto-draw="!reduceMotion"
                :auto-draw-duration="350"
                stroke-linecap="round"
                aria-hidden="true"
              />
            </div>
            <p v-else class="vds-graph__empty text-caption mb-0">No data points in this range.</p>
          </div>
        </v-col>
      </v-row>
    </gg-card-text>
  </gg-card>
</template>

<script>
import kvm from '@/api/kvm'
import { kvmSize } from '@/views/services/hosting/kvm/kvmFormat'

export default {
  name: 'HostingKvmGraphs',
  props: {
    server: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    ranges: ['1h', '24h', '7d', '30d', '1y'],
    rangeLabels: {
      '1h': 'Last 1 hour',
      '24h': 'Last 24 hours',
      '7d': 'Last 7 days',
      '30d': 'Last 30 days',
      '1y': 'Last year',
    },
    range: '1h',
    metrics: null,
    loadingMetrics: false,
    error: null,
    reduceMotion: false,
  }),
  computed: {
    capable () {
      return !!(this.server.capabilities && this.server.capabilities.metrics_history)
    },
    // Theme-aware, on-brand (green) gradient. Both stops clear the 3:1 contrast
    // floor for graphical objects on each theme's surface, replacing the
    // off-brand teal/blue gradient.
    gradient () {
      return this.$vuetify.theme.dark
        ? ['#3db14e', '#19c463']
        : ['#0a7c3c', '#0ca750']
    },
    statusMessage () {
      if (!this.capable) return ''
      if (this.loadingMetrics) return 'Loading metrics…'
      if (this.error) return ''
      return `Metrics updated for ${this.rangeLabels[this.range] || this.range}.`
    },
    graphs () {
      const m = this.metrics || {}
      const series = (key) => (m[key] || []).map(p => (p[1] == null ? 0 : p[1]))
      const last = (key) => {
        const pts = (m[key] || []).filter(p => p[1] != null)
        return pts.length ? pts[pts.length - 1][1] : null
      }
      const pct = v => (v == null ? '—' : `${Math.round(v * 100)}%`)
      const size = v => (v == null ? '—' : kvmSize(v))
      // /vm/metrics reports net/disk-IO as per-second rates (bytes/sec) — NOT the
      // cumulative-since-boot counters /vm/status returns — so label them as rates.
      const rate = v => (v == null ? '—' : `${kvmSize(v)}/s`)
      return [
        { key: 'cpu', title: 'CPU usage', values: series('cpu').map(v => v * 100), current: pct(last('cpu')) },
        { key: 'mem', title: 'Memory usage', values: series('mem'), current: size(last('mem')) },
        { key: 'netin', title: 'Network in', values: series('netin'), current: rate(last('netin')) },
        { key: 'netout', title: 'Network out', values: series('netout'), current: rate(last('netout')) },
        { key: 'diskread', title: 'Disk read', values: series('diskread'), current: rate(last('diskread')) },
        { key: 'diskwrite', title: 'Disk write', values: series('diskwrite'), current: rate(last('diskwrite')) },
      ]
    },
  },
  methods: {
    // Accessible text alternative for each sparkline (the SVG itself is decorative).
    chartLabel (g) {
      const range = this.rangeLabels[this.range] || this.range
      const current = g.current && g.current !== '—' ? g.current : 'unavailable'
      // The visible text already shows the current value; the chart's unique
      // info is the TREND, so summarise min/max + direction for AT.
      let trend = ''
      const vals = g.values || []
      if (vals.length) {
        const fmt = (v) => {
          if (g.key === 'cpu') return `${Math.round(v)}%`
          if (g.key === 'mem') return kvmSize(v)
          return `${kvmSize(v)}/s`
        }
        const min = Math.min(...vals)
        const max = Math.max(...vals)
        const span = max - min
        const delta = vals[vals.length - 1] - vals[0]
        let dir = 'steady'
        if (span > 0 && Math.abs(delta) > span * 0.1) {
          dir = delta > 0 ? 'trending up' : 'trending down'
        }
        trend = ` Min ${fmt(min)}, max ${fmt(max)}, ${dir}.`
      }
      return `${g.title} over ${range}. Current value: ${current}.${trend}`
    },
    async loadMetrics () {
      if (!this.capable || !this.server.id) return
      try {
        this.loadingMetrics = true
        this.error = null
        this.metrics = await kvm.getMetrics(this.server.id, this.range)
      } catch (e) {
        this.error = e
        this.metrics = null
      } finally {
        this.loadingMetrics = false
      }
    },
  },
  created () {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
    this.loadMetrics()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.vds-metrics__heading {
  margin: 0;
}

.vds-metrics__sr-status {
  @include kvm-sr-only;
}

.vds-metrics__loading {
  @include kvm-secondary-text;
}

.vds-graph {
  border: 1px solid $kvm-border-light;
  border-radius: 6px;

  .theme--dark & {
    border-color: $kvm-border-dark;
  }
}

.vds-graph__current,
.vds-graph__empty {
  @include kvm-secondary-text;
}

// Sparkline is theme-aware via the bound gradient; clamp its height so charts
// never overflow their tile at narrow (320px) widths.
.vds-graph__chart {
  ::v-deep .v-sparkline {
    display: block;
    width: 100%;
    height: auto;
  }
}

// Range toggle: group semantics live in the template; here we give the buttons a
// comfortable target, a visible focus ring, and a selected state that does NOT
// rely on color alone (solid fill + bold weight), contrast-safe in both themes.
.vds-metrics__range {
  ::v-deep .v-btn {
    min-height: 36px;

    // Toggle buttons sit flush, so the offset ring's left/right edges would be
    // overpainted by neighbours; raise the focused button to keep it whole.
    &:focus-visible {
      position: relative;
      z-index: 1;
      outline: 2px solid $kvm-focus-light;
      outline-offset: 2px;
    }

    &.v-btn--active {
      background-color: $kvm-focus-light;
      color: #fff;
      font-weight: 700;
    }

    &.v-btn--active::before {
      opacity: 0;
    }

    &.v-btn--active .v-btn__content {
      color: #fff;
    }
  }

  .theme--dark & ::v-deep .v-btn {
    &:focus-visible {
      outline-color: $kvm-focus-dark;
    }

    &.v-btn--active {
      background-color: $kvm-focus-dark;
      color: #00150a;
    }

    &.v-btn--active .v-btn__content {
      color: #00150a;
    }
  }
}
</style>
